import { z } from 'zod';
import { CONTACT_REQUIREMENTS } from './config';

/**
 * Quick start: the smallest question that still produces a useful research run.
 *
 * The full wizard stays available for everything else — this exists so that someone who has
 * never seen the app can type what they want and get results, without first learning what an
 * "attribute layer" or a "merge threshold" is. Every other setting is filled in from the same
 * defaults the wizard would have offered, and the created project is a perfectly ordinary
 * project: it can be re-opened, edited and re-run in the wizard afterwards.
 */
export const QuickStartSchema = z.object({
  /** What to look for — a business type or keyword, e.g. "dentists", "marketing agency". */
  what: z.string().trim().min(2, 'Say what kind of business to look for.').max(120),
  /** Where to look — a city or district, e.g. "Alexandria", "Smouha". */
  where: z.string().trim().min(2, 'Say which city or area to search.').max(120),
  country: z.string().regex(/^[A-Z]{2}$/, 'Choose a country.'),
  /** How many businesses to aim for per source. */
  target: z.number().int().min(10).max(5000).default(200),
  contactRequirement: z.enum(CONTACT_REQUIREMENTS).default('phone_or_email'),
  /** Start the run immediately (the default) rather than only creating the project. */
  start: z.boolean().default(true),
});
export type QuickStartRequest = z.input<typeof QuickStartSchema>;

export interface QuickStartResponse {
  projectId: string;
  jobId: string | null;
  /** Sources that were actually usable for this workspace right now. */
  sources: string[];
  /** Plain-language summary of what was set up, shown back to the user. */
  summary: string;
}

/** A ready-made quick-start idea, offered as a one-click starting point. */
export interface QuickStartIdea {
  label: string;
  what: string;
}

/**
 * Starting points for someone with a blank page. Deliberately generic business types that
 * exist in most cities, so the first run returns something no matter where the user is.
 */
export const QUICK_START_IDEAS: readonly QuickStartIdea[] = [
  { label: 'Restaurants', what: 'restaurant' },
  { label: 'Dentists', what: 'dentist' },
  { label: 'Gyms', what: 'gym' },
  { label: 'Marketing agencies', what: 'marketing agency' },
  { label: 'Hotels', what: 'hotel' },
  { label: 'Pharmacies', what: 'pharmacy' },
  { label: 'Car repair', what: 'car repair' },
  { label: 'Real estate', what: 'real estate agency' },
];
